import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { TemplateUploadForm } from '../../components/studio/TemplateUploadForm';
import { useTemplates } from '../../hooks/useTemplates';

export function TemplateListPage() {
  const { t } = useTranslation();
  const { templates, categories, loading, error } = useTemplates();

  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name ?? id;

  return (
    <>
      <h1 className="text-3xl font-heading font-semibold tracking-normal text-foreground">
        {t('studio.templates.title')}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">{t('studio.templates.subtitle')}</p>

      <TemplateUploadForm categories={categories} />

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {!loading && !error && templates.length === 0 ? (
        <section className="mt-6 rounded-lg border border-border bg-surface p-6">
          <div className="grid size-10 place-items-center rounded bg-accent text-accent-foreground">
            <DocumentTextIcon className="size-5" aria-hidden="true" />
          </div>
          <h2 className="mt-3 text-base font-semibold text-foreground">{t('studio.templates.emptyTitle')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('studio.templates.emptyBody')}</p>
        </section>
      ) : null}

      {!loading && !error && templates.length > 0 ? (
        <div className="mt-6 overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted">
              <tr>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('studio.templates.columns.name')}</th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('studio.templates.columns.category')}</th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('studio.templates.columns.type')}</th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('studio.templates.columns.properties')}</th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('studio.templates.columns.status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-muted">
                  <td className="px-4 py-3">
                    <Link
                      to={`/studio/templates/${template.id}`}
                      className="font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                      {template.name}
                    </Link>
                    <p className="mt-0.5 max-w-md truncate text-muted-foreground">{template.description}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{categoryName(template.categoryId)}</td>
                  <td className="whitespace-nowrap px-4 py-3 uppercase text-muted-foreground">{template.documentType}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{template.propertyCount}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      {template.isPublished ? t('studio.templates.published') : t('studio.templates.draft')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
